import {
  Outlet,
  useMatchRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { BottomNav } from "@/components/layout/bottom-nav";
import { SideNav } from "@/components/layout/side-nav";
import { useLanguage } from "@/contexts/language";
import { ChatsPage } from "@/features/chat/chats-page";
import { useInboxRealtime } from "@/features/chat/use-inbox-realtime";
import { cn } from "@/lib/utils";
import { useSwipeBack } from "@/hooks/use-swipe-back";
import { isNative } from "@/lib/capacitor";
import {
  checkNotificationPermission,
  initNotifications,
  notificationsEnabled,
  requestNotificationPermission,
  setNotificationOpenHandler,
} from "@/lib/notifications";
import { reportDeviceInfo } from "@/lib/device-info";
import { initPush, setPushOpenHandler } from "@/lib/push";
import { reconcileMyPayments } from "@/api/billing";
import { loadSession, useSession } from "@/session";

/**
 * Turns an incoming WhatsApp link into the new-delivery route, or null for
 * anything that isn't one. Handles the shapes the chooser can hand us:
 *   https://wa.me/9665xxxxxxxx[?text=...]
 *   https://api.whatsapp.com/send?phone=9665xxxxxxxx
 *   whatsapp://send?phone=9665xxxxxxxx
 */
function whatsAppLinkTarget(url: string): string | null {
  const isWhatsApp =
    /^whatsapp:/i.test(url) ||
    /^https?:\/\/(www\.)?(wa\.me|([a-z]+\.)?whatsapp\.com)\//i.test(url);
  if (!isWhatsApp) return null;
  const match =
    /wa\.me\/(?:%2B|\+)?(\d{5,15})/i.exec(url) ??
    /[?&]phone=(?:%2B|\+)?(\d{5,15})/i.exec(url);
  return match ? `/chats/quick?phone=${match[1]}` : "/chats/quick";
}

// Authenticated layout: content area padded for the fixed bottom nav
// (64px + safe area) on phones, or offset by the 240px SideNav rail from md
// up. The realtime socket lives here so every authenticated
// screen gets live inbox updates (list, counts badge, open thread).
export function AppShell() {
  const { dir } = useLanguage();
  const navigate = useNavigate();
  const driverId = useSession()?.driver.id;
  useInboxRealtime();

  // WhatsApp-style desktop chat layout: on the conversations list and thread
  // screens the nav collapses to an icon rail (logo kept on top) and the
  // conversations list becomes a persistent aside beside the content. The
  // /chats/new and /chats/import flows keep the standard full-width layout.
  const matchRoute = useMatchRoute();
  const threadMatch = matchRoute({ to: "/chats/$conversationId" });
  // The /chats/new, /chats/quick and /chats/import routes match the thread
  // pattern too.
  const inThread =
    !!threadMatch &&
    threadMatch.conversationId !== "new" &&
    threadMatch.conversationId !== "quick" &&
    threadMatch.conversationId !== "import";
  const chatLayout = !!matchRoute({ to: "/chats" }) || inThread;
  // Edge-swipe back (and Android's system back). The content column is what
  // slides under the finger; the bottom nav stays put, like a native tab bar.
  const { contentRef } = useSwipeBack();

  // Tapping a message notification opens that thread — same handler whether it
  // came from a local notification or a OneSignal push. Bound here rather than
  // in main.tsx so it only exists once the driver is authenticated.
  useEffect(() => {
    const open = (conversationId: string) => {
      void navigate({
        to: "/chats/$conversationId",
        params: { conversationId },
      });
    };
    setNotificationOpenHandler(open);
    setPushOpenHandler(open);
    void initNotifications();
  }, [navigate]);

  // Binds this device to the driver in OneSignal (external id = driver id), so
  // the API can push to them by id. No-op without VITE_ONESIGNAL_APP_ID.
  useEffect(() => {
    if (driverId) void initPush(driverId);
  }, [driverId]);

  // Silent device report (model, OS, app version, push permission, language,
  // region) once the driver is known. Repeats below after the permission
  // prompt and on native resume (the OS settings may have changed it).
  useEffect(() => {
    if (driverId) void reportDeviceInfo();
  }, [driverId]);

  // Deep links (ge.iehasa.mandoob://…): the browser-side payment pages send
  // the driver back here — route the path straight into the router, e.g.
  // "…://plans/success?paymentId=x" -> /plans/success?paymentId=x. Handles
  // both a running app (appUrlOpen) and a cold start (launch url).
  // WhatsApp links (the manifest registers the app for wa.me / whatsapp://)
  // get their own parse: they open "new delivery" with the number prefilled.
  const router = useRouter();
  useEffect(() => {
    if (!isNative()) return;
    const openDeepLink = (url: string | undefined) => {
      if (!url) return;
      const whatsAppTarget = whatsAppLinkTarget(url);
      if (whatsAppTarget) {
        router.history.push(whatsAppTarget);
        return;
      }
      const marker = "://";
      const at = url.indexOf(marker);
      if (at < 0) return;
      const target = `/${url.slice(at + marker.length)}`;
      router.history.push(target);
    };
    let remove: (() => void) | null = null;
    let disposed = false;
    void import("@capacitor/app").then(async ({ App }) => {
      const launch = await App.getLaunchUrl().catch(() => null);
      openDeepLink(launch?.url);
      const handle = await App.addListener("appUrlOpen", (event) =>
        openDeepLink(event.url),
      );
      if (disposed) void handle.remove();
      else remove = () => void handle.remove();
    });
    return () => {
      disposed = true;
      remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Native resume: the driver may be returning from a browser checkout —
  // reconcile pending payments server-side and refresh the balance, so
  // credits land even without the deep link or a webhook.
  useEffect(() => {
    if (!isNative()) return;
    let remove: (() => void) | null = null;
    let disposed = false;
    void import("@capacitor/app").then(async ({ App }) => {
      const handle = await App.addListener("resume", () => {
        void reconcileMyPayments()
          .then(({ credited }) => {
            if (credited > 0) void loadSession().catch(() => undefined);
          })
          .catch(() => undefined);
        void reportDeviceInfo();
      });
      if (disposed) void handle.remove();
      else remove = () => void handle.remove();
    });
    return () => {
      disposed = true;
      remove?.();
    };
  }, []);

  // Native app launch: raise the OS notification-permission prompt right away
  // (Android 13+ blocks notifications until it is granted). Only while the
  // state is still undecided — a granted or denied answer is never re-asked,
  // and the in-app mute preference is respected. Browsers are excluded: a
  // page-load prompt is ignored (or permanently denied) there, so the web
  // keeps asking from the settings toggle instead.
  useEffect(() => {
    if (!isNative()) return;
    void (async () => {
      if (!notificationsEnabled()) return;
      const state = await checkNotificationPermission();
      if (state === "prompt") {
        await requestNotificationPermission();
        void reportDeviceInfo();
      }
    })();
  }, []);

  return (
    <div
      dir={dir}
      className={cn(
        "tw:min-h-dvh tw:bg-background",
        chatLayout ? "tw:md:ps-[27rem]" : "tw:md:ps-60",
      )}
    >
      <div
        ref={contentRef}
        className={cn(
          "tw:mx-auto tw:flex tw:h-dvh tw:w-full tw:max-w-xl tw:flex-col tw:md:max-w-none tw:md:pb-0",
          inThread
            ? "tw:pb-[env(safe-area-inset-bottom)]"
            : "tw:pb-[calc(64px+env(safe-area-inset-bottom))]",
        )}
      >
        <Outlet />
      </div>
      {!inThread && <BottomNav />}
      <SideNav collapsed={chatLayout} />
      {chatLayout ? (
        <aside className="tw:fixed tw:inset-y-0 tw:start-[4.5rem] tw:z-40 tw:hidden tw:w-[22.5rem] tw:flex-col tw:border-e tw:border-border tw:bg-background tw:md:flex">
          <ChatsPage />
        </aside>
      ) : null}
    </div>
  );
}
